import { CheckIcon } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardTitle,
  CardFooter,
  CardHeader,
  CardContent,
  CardDescription,
} from "@/components/ui/card";

export interface PricingTier {
  name: string;
  price: string;
  priceYearly?: string;
  description: string;
  features: string[];
  buttonText: string;
  buttonVariant?: "default" | "outline";
  popular?: boolean;
  stripePriceId?: string;
  stripePriceYearlyId?: string;
  freeTrialDays?: number;
}

interface PricingCardProps {
  tier: PricingTier;
  isYearly?: boolean;
  onStartFreeTrial?: (tier: PricingTier, isYearly: boolean) => void;
  loading?: boolean;
}

export function PricingCard({
  tier,
  isYearly = false,
  onStartFreeTrial,
  loading = false,
}: PricingCardProps) {
  return (
    <Card
      className={`h-full overflow-hidden ${
        tier.popular ? "border-primary shadow-md relative" : "border-border"
      }`}
    >
      {tier.popular && (
        <Badge className="absolute top-0 right-0 rounded-bl-md rounded-tr-md rounded-tl-none rounded-br-none px-3 py-1.5">
          Most Popular
        </Badge>
      )}

      <CardHeader className="bg-card border-b border-border">
        <CardTitle className="text-xl">{tier.name}</CardTitle>
        <div className="flex items-baseline mt-2">
          <span className="text-3xl font-bold">
            {isYearly && tier.priceYearly ? tier.priceYearly : tier.price}
          </span>
          {tier.price !== "Custom" && (
            <span className="text-muted-foreground ml-1">
              {/* {isYearly ? "/month" : "/month"} */}
              {isYearly ? "/month" : "/month"}
            </span>
          )}
        </div>
        {isYearly && tier.price !== "Custom" && (
          <div className="text-primary text-sm font-medium mt-1">
            Save{" "}
            {Math.round(
              ((parseInt(tier.price.replace("$", "")) * 12 -
                parseInt(tier.priceYearly?.replace("$", "") || "0")) /
                (parseInt(tier.price.replace("$", "")) * 12)) *
                100
            )}
            %
          </div>
        )}
        {tier.freeTrialDays && (
          <div className="mt-2 bg-primary/10 text-primary text-sm px-2 py-1 rounded-md inline-block">
            {tier.freeTrialDays}-day free trial
          </div>
        )}
        <CardDescription className="mt-2">{tier.description}</CardDescription>
      </CardHeader>

      <CardContent className="p-6 min-h-[400px]">
        <ul className="space-y-3 text-sm">
          {tier.features.map((feature, i) => (
            <li key={i} className="flex">
              <CheckIcon className="h-5 w-5 text-primary shrink-0 mr-2" />
              <span>{feature}</span>
            </li>
          ))}
        </ul>
      </CardContent>

      <CardFooter className="bg-card p-6 border-t border-border">
        <Button
          className="w-full"
          variant={tier.buttonVariant || "default"}
          size="lg"
          onClick={() => onStartFreeTrial?.(tier, isYearly)}
          disabled={loading}
        >
          {loading ? "Processing..." : tier.buttonText}
        </Button>
      </CardFooter>
    </Card>
  );
}
